import React, { useState, useEffect } from 'react';
import { Alert, Button, Form, InputGroup } from '@/components/ui';
import ResponsiveModal from '../../../components/ResponsiveModal';
import type { Classification } from '../../../types/Employee';
import type { ServiceArea } from '../../../services/areasService';
import { usePermissions } from '../../../hooks/usePermissions';
import { useAuth } from '../../../contexts/AuthContext';
import { describeActionError } from '../../../utils/loadError';

type ClassificationField = 'code' | 'rate' | 'serviceAreaId';
const FIELD_ORDER: ClassificationField[] = ['code', 'rate', 'serviceAreaId'];
const FIELD_ID: Record<ClassificationField, string> = {
  code: 'classificationCode',
  rate: 'classificationRate',
  serviceAreaId: 'classificationServiceArea',
};

interface ClassificationModalProps {
  show: boolean;
  onHide: () => void;
  classification: Classification | null;
  serviceAreas: ServiceArea[];
  onSave: (classificationData: Partial<Classification>) => Promise<void>;
}

const ClassificationModal: React.FC<ClassificationModalProps> = ({
  show,
  onHide,
  classification,
  serviceAreas,
  onSave
}) => {
  const [formData, setFormData] = useState({
    code: '',
    rate: '',
    serviceAreaId: 0
  });
  const [errors, setErrors] = useState<Partial<Record<ClassificationField, string>>>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const { hasPermission } = usePermissions();
  const { currentUser } = useAuth();
  
  // Check if user is admin
  const isAdmin = hasPermission('user:edit') || hasPermission('user:create') || currentUser?.role?.name === 'Admin';
  const isReadOnly = !hasPermission('classification:edit') && !isAdmin;

  useEffect(() => {
    if (show) {
      setErrors({});
      setSaveError(null);
      if (classification) {
        setFormData({
          code: classification.code || '',
          rate: classification.rate ? classification.rate.toFixed(2) : '',
          serviceAreaId: classification.serviceAreaId || 0
        });
      } else {
        setFormData({
          code: '',
          rate: '',
          serviceAreaId: 0
        });
      }
    }
  }, [show, classification]);

  const updateField = (field: ClassificationField, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isReadOnly) return;
    setSaveError(null);

    // Each problem shows under its field (FORM-01)
    const fieldErrors: Partial<Record<ClassificationField, string>> = {};
    const rate = parseFloat(formData.rate);
    if (!formData.code.trim()) fieldErrors.code = 'Code required';
    if (!formData.rate) {
      fieldErrors.rate = 'Rate required';
    } else if (isNaN(rate) || rate < 0) {
      fieldErrors.rate = 'Rate must be a number of 0 or more';
    }
    if (!formData.serviceAreaId) fieldErrors.serviceAreaId = 'Service area required';
    if (Object.keys(fieldErrors).length > 0) {
      setErrors(fieldErrors);
      const first = FIELD_ORDER.find(f => fieldErrors[f]);
      if (first) document.getElementById(FIELD_ID[first])?.focus();
      return;
    }

    try {
      await onSave({
        code: formData.code,
        rate: rate,
        serviceAreaId: formData.serviceAreaId
      });
      onHide();
    } catch (error: any) {
      console.error('Error saving classification:', error);
      setSaveError(describeActionError(error, "Couldn't save the classification. Try again."));
    }
  };

  const handleCancel = () => {
    onHide();
  };

  return (
    <ResponsiveModal
      isOpen={show}
      onClose={onHide}
      title={classification ? 'Edit Classification' : 'Add Classification'}
      size="medium"
      footer={
        <>
          <Button variant="secondary" type="button" onClick={handleCancel} className="tw-mr-2">
            Cancel
          </Button>
          {!isReadOnly && (
            <Button variant="primary" type="submit" form="classificationForm">
              {classification ? 'Save Changes' : 'Create Classification'}
            </Button>
          )}
        </>
      }
    >
      <Form onSubmit={handleSubmit} id="classificationForm" noValidate>
        {saveError && (
          <Alert variant="danger" role="alert" className="tw-mb-3">
            {saveError}
          </Alert>
        )}
        <Form.Group className="tw-mb-3" controlId={FIELD_ID.code}>
          <Form.Label>Code <span className="tw-text-danger">*</span></Form.Label>
          <Form.Control
            type="text"
            value={formData.code}
            onChange={e => !isReadOnly && updateField('code', e.target.value)}
            readOnly={isReadOnly}
            required
            isInvalid={!!errors.code}
            placeholder="e.g., CL1, CL2"
          />
          {errors.code && <Form.Control.Feedback type="invalid">{errors.code}</Form.Control.Feedback>}
          <Form.Text className="tw-text-content-muted">Classification code (not unique)</Form.Text>
        </Form.Group>

        <Form.Group className="tw-mb-3" controlId={FIELD_ID.rate}>
          <Form.Label>Rate <span className="tw-text-danger">*</span></Form.Label>
          <InputGroup>
            <InputGroup.Text>$</InputGroup.Text>
            <Form.Control
              type="number"
              step="0.01"
              min="0"
              value={formData.rate}
              onChange={e => !isReadOnly && updateField('rate', e.target.value)}
              onBlur={e => {
                // Format to 2 decimal places on blur
                if (e.target.value && !isNaN(parseFloat(e.target.value))) {
                  const numValue = parseFloat(e.target.value);
                  if (!isNaN(numValue) && numValue >= 0) {
                    setFormData({...formData, rate: numValue.toFixed(2)});
                  }
                }
              }}
              readOnly={isReadOnly}
              required
              isInvalid={!!errors.rate}
              placeholder="0.00"
            />
          </InputGroup>
          {errors.rate && <Form.Control.Feedback type="invalid">{errors.rate}</Form.Control.Feedback>}
          <Form.Text className="tw-text-content-muted">Pay rate for this classification</Form.Text>
        </Form.Group>

        <Form.Group className="tw-mb-3" controlId={FIELD_ID.serviceAreaId}>
          <Form.Label>Service Area <span className="tw-text-danger">*</span></Form.Label>
          <Form.Select
            value={formData.serviceAreaId}
            onChange={e => !isReadOnly && updateField('serviceAreaId', parseInt(e.target.value))}
            disabled={isReadOnly}
            required
            isInvalid={!!errors.serviceAreaId}
          >
            <option value="0">Select a service area</option>
            {serviceAreas.map(area => (
              <option key={area.id} value={area.id}>
                {area.name}
              </option>
            ))}
          </Form.Select>
          {errors.serviceAreaId && <Form.Control.Feedback type="invalid">{errors.serviceAreaId}</Form.Control.Feedback>}
        </Form.Group>
      </Form>
    </ResponsiveModal>
  );
};

export default ClassificationModal;
